import { buildODataQuery, createPollingInterval } from "@sg-protect/core";
import { mutationOptions, queryOptions, useQuery } from "../client.ts";
import type {
    ODataDoNotDisturbUserODataCollectionResponse,
    ODataDoNotDisturbWorkspaceODataCollectionResponse,
    ReviewActivationStateResponse,
    ReviewMaterializationStateResponse,
    ReviewResponse
} from "../types.ts";
import { usePollingInterval } from "../utils/usePollingInterval.ts";

const isMaterializationStale = (data: ReviewMaterializationStateResponse) =>
    data.state !== "completed" && data.state !== "failed";

const isActivationStale = (data: ReviewActivationStateResponse) =>
    data.state !== "completed" && data.state !== "failed";

const activationRefetchInterval = createPollingInterval<ReviewActivationStateResponse>({
    isPending: data => data != null && isActivationStale(data),
    timeout: Infinity
});

export type { ReviewResponse };

export function reviewsSummaryQueryOptions() {
    return queryOptions("get", "/bff/reviews/summary");
}

export function reviewQueryOptions(reviewId: string) {
    return queryOptions("get", "/bff/reviews/{reviewId}", { params: { path: { reviewId } } });
}

interface ODataReviewQueryOptions {
    filter?: string;
    skip?: number;
    top?: number;
}

export function reviewNotificationRecipientsQueryOptions({
    reviewId,
    filter,
    skip,
    top
}: ODataReviewQueryOptions & { reviewId: string }) {
    const combinedFilter = filter ? `reviewId eq ${reviewId} and (${filter})` : `reviewId eq ${reviewId}`;

    return queryOptions("get", "/bff/odata/reviewNotificationRecipients", {
        params: {
            query: buildODataQuery({
                select: ["id", "displayName", "email", "userPrincipalName"],
                filter: combinedFilter,
                orderby: { field: "displayName", direction: "asc" },
                ...(skip !== undefined && { skip }),
                ...(top !== undefined && { top })
            })
        }
    });
}

export function reviewODataDetailQueryOptions(reviewId: string) {
    return queryOptions("get", "/bff/odata/reviews", {
        params: {
            query: buildODataQuery({
                select: [
                    "id",
                    "title",
                    "status",
                    "startedAt",
                    "expireAt",
                    "reviewTypes",
                    "totalReviewItemsCount",
                    "totalReviewersCount",
                    "completedReviewItemsCount"
                ],
                filter: `id eq ${reviewId}`,
                top: 1
            })
        }
    });
}

export function createReviewMutationOptions() {
    return mutationOptions("post", "/bff/reviews", {
        onSuccess: async (_data, _variables, _onMutateResult, { client }) => {
            await Promise.all([
                client.invalidateQueries({ queryKey: reviewsSummaryQueryOptions().queryKey }),
                client.invalidateQueries({ queryKey: queryOptions("get", "/bff/odata/reviews").queryKey })
            ]);
        }
    });
}

export function updateReviewMutationOptions() {
    return mutationOptions("patch", "/bff/reviews/{reviewId}", {
        onSuccess: async (_data, variables, _onMutateResult, { client }) => {
            await Promise.all([
                client.invalidateQueries({ queryKey: reviewsSummaryQueryOptions().queryKey }),
                client.invalidateQueries({ queryKey: queryOptions("get", "/bff/odata/reviews").queryKey }),
                client.invalidateQueries({
                    queryKey: reviewQueryOptions(variables.params.path.reviewId).queryKey
                })
            ]);
        }
    });
}

export function deleteReviewMutationOptions() {
    return mutationOptions("delete", "/bff/reviews/{reviewId}", {
        onSuccess: async (_data, variables, _onMutateResult, { client }) => {
            // Removed rather than invalidated: the review is gone, so refetching its detail would 404.
            client.removeQueries({
                queryKey: reviewQueryOptions(variables.params.path.reviewId).queryKey
            });

            await Promise.all([
                client.invalidateQueries({ queryKey: reviewsSummaryQueryOptions().queryKey }),
                client.invalidateQueries({ queryKey: queryOptions("get", "/bff/odata/reviews").queryKey })
            ]);
        }
    });
}

export function sendReviewTestEmailMutationOptions() {
    return mutationOptions("post", "/bff/reviews/sendTestEmail");
}

export function addDoNotDisturbUserMutationOptions() {
    return mutationOptions("post", "/bff/reviews/doNotDisturbUsers", {
        onSettled: async (_data, _error, _variables, _onMutateResult, { client }) => {
            await Promise.all([
                client.invalidateQueries({ queryKey: queryOptions("get", "/bff/odata/doNotDisturbUsers").queryKey }),
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/odata/reviewSelectedWorkspaces").queryKey
                }),
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/odata/reviewNotificationRecipients").queryKey
                })
            ]);
        }
    });
}

export function removeDoNotDisturbUserMutationOptions() {
    return mutationOptions("delete", "/bff/reviews/doNotDisturbUsers/{id}", {
        onSettled: async (_data, _error, _variables, _onMutateResult, { client }) => {
            await Promise.all([
                client.invalidateQueries({ queryKey: queryOptions("get", "/bff/odata/doNotDisturbUsers").queryKey }),
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/odata/reviewSelectedWorkspaces").queryKey
                }),
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/odata/reviewNotificationRecipients").queryKey
                })
            ]);
        }
    });
}

export function addDoNotDisturbWorkspaceMutationOptions() {
    return mutationOptions("post", "/bff/reviews/doNotDisturbWorkspaces", {
        onSettled: async (_data, _error, _variables, _onMutateResult, { client }) => {
            await Promise.all([
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/odata/doNotDisturbWorkspaces").queryKey
                }),
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/odata/reviewSelectedWorkspaces").queryKey
                }),
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/odata/reviewNotificationRecipients").queryKey
                })
            ]);
        }
    });
}

export function removeDoNotDisturbWorkspaceMutationOptions() {
    return mutationOptions("delete", "/bff/reviews/doNotDisturbWorkspaces/{id}", {
        onSettled: async (_data, _error, _variables, _onMutateResult, { client }) => {
            await Promise.all([
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/odata/doNotDisturbWorkspaces").queryKey
                }),
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/odata/reviewSelectedWorkspaces").queryKey
                }),
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/odata/reviewNotificationRecipients").queryKey
                })
            ]);
        }
    });
}

export function addReviewItemsMutationOptions() {
    return mutationOptions("post", "/bff/reviews/{reviewId}/items", {
        onSettled: async (_data, _error, variables, _onMutateResult, { client }) => {
            await Promise.all([
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/odata/reviewSelectedWorkspaces").queryKey
                }),
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/odata/reviewNotificationRecipients").queryKey
                }),
                client.invalidateQueries({
                    queryKey: reviewQueryOptions(variables.params.path.reviewId).queryKey
                })
            ]);
        }
    });
}

export function removeReviewItemsMutationOptions() {
    return mutationOptions("post", "/bff/reviews/{reviewId}/items/remove", {
        onSettled: async (_data, _error, variables, _onMutateResult, { client }) => {
            await Promise.all([
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/odata/reviewSelectedWorkspaces").queryKey
                }),
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/odata/reviewNotificationRecipients").queryKey
                }),
                client.invalidateQueries({
                    queryKey: reviewQueryOptions(variables.params.path.reviewId).queryKey
                })
            ]);
        }
    });
}

export function resendNotificationMutationOptions() {
    return mutationOptions("post", "/bff/reviews/resendNotification", {
        onSuccess: async (_data, _variables, _onMutateResult, { client }) => {
            await client.invalidateQueries({
                queryKey: queryOptions("get", "/bff/odata/reviewNotificationRecipients").queryKey
            });
        }
    });
}

export function closeReviewMutationOptions() {
    return mutationOptions("post", "/bff/reviews/{reviewId}/close", {
        onSuccess: async (_data, variables, _onMutateResult, { client }) => {
            await Promise.all([
                client.invalidateQueries({ queryKey: reviewsSummaryQueryOptions().queryKey }),
                client.invalidateQueries({ queryKey: queryOptions("get", "/bff/odata/reviews").queryKey }),
                client.invalidateQueries({
                    queryKey: reviewQueryOptions(variables.params.path.reviewId).queryKey
                })
            ]);
        }
    });
}

export function activateReviewMutationOptions() {
    return mutationOptions("post", "/bff/reviews/{reviewId}/activate", {
        onSuccess: async (_data, variables, _onMutateResult, { client }) => {
            await Promise.all([
                client.invalidateQueries({ queryKey: reviewsSummaryQueryOptions().queryKey }),
                client.invalidateQueries({ queryKey: queryOptions("get", "/bff/odata/reviews").queryKey }),
                client.invalidateQueries({
                    queryKey: reviewQueryOptions(variables.params.path.reviewId).queryKey
                })
            ]);
        }
    });
}

interface PaginationParams {
    skip: number;
    top: number;
}

interface DraftReviewsParams extends PaginationParams {
    search?: string;
}

export function draftReviewsQueryOptions({ skip, top, search }: DraftReviewsParams) {
    return queryOptions("get", "/bff/odata/reviews", {
        params: {
            query: buildODataQuery({
                select: ["id", "title", "createdAt"],
                filter: "status eq 'draft'",
                search,
                orderby: { field: "createdAt", direction: "desc" },
                skip,
                top
            })
        }
    });
}

interface ReviewsListParams extends PaginationParams {
    status: "active" | "closed" | "draft";
}

export function reviewsListQueryOptions({ status, skip, top }: ReviewsListParams) {
    const query = buildODataQuery({
        select: [
            "id",
            "title",
            "status",
            "expireAt",
            "completedReviewItemsCount",
            "inProgressReviewItemsCount",
            "totalReviewItemsCount",
            "totalReviewersCount"
        ],
        filter: `status eq '${status}'`,
        orderby:
            status === "closed" ? { field: "closedAt", direction: "desc" } : { field: "expireAt", direction: "asc" },
        skip,
        top
    });

    query.$orderby = `${query.$orderby},id asc`;

    return queryOptions("get", "/bff/odata/reviews", { params: { query } });
}

function reviewMaterializationStateQueryOptions(reviewId: string) {
    return queryOptions("get", "/bff/reviews/{reviewId}/materializationState", { params: { path: { reviewId } } });
}

export function usePollReviewMaterializationStateQuery(reviewId: string) {
    const { refetchInterval } = usePollingInterval<ReviewMaterializationStateResponse>({
        isPending: data => data != null && isMaterializationStale(data),
        timeout: 60_000
    });

    return useQuery(
        "get",
        "/bff/reviews/{reviewId}/materializationState",
        { params: { path: { reviewId } } },
        {
            ...reviewMaterializationStateQueryOptions(reviewId),
            refetchInterval
        }
    );
}

function reviewActivationStateQueryOptions(reviewId: string) {
    return queryOptions("get", "/bff/reviews/{reviewId}/activationState", { params: { path: { reviewId } } });
}

export function usePollReviewActivationStateQuery(reviewId: string) {
    return useQuery(
        "get",
        "/bff/reviews/{reviewId}/activationState",
        { params: { path: { reviewId } } },
        {
            ...reviewActivationStateQueryOptions(reviewId),
            refetchInterval: activationRefetchInterval
        }
    );
}

export function reviewItemQueryOptions({ reviewId, reviewItemId }: { reviewId: string; reviewItemId: string }) {
    return queryOptions("get", "/bff/reviews/{reviewId}/items/{reviewItemId}", {
        params: { path: { reviewId, reviewItemId } }
    });
}

function selectExcludedUserIds(data: ODataDoNotDisturbUserODataCollectionResponse) {
    return (data.value ?? []).map(u => u.userId);
}

const excludedUserIdsQuery = buildODataQuery({ select: ["userId"], count: false });

export function excludedUserIdsQueryOptions() {
    return queryOptions(
        "get",
        "/bff/odata/doNotDisturbUsers",
        { params: { query: excludedUserIdsQuery } },
        { select: selectExcludedUserIds }
    );
}

function selectExcludedWorkspaceIds(data: ODataDoNotDisturbWorkspaceODataCollectionResponse) {
    return (data.value ?? []).map(w => w.siteCollectionId);
}

const excludedWorkspaceIdsQuery = buildODataQuery({ select: ["siteCollectionId"], count: false });

export function excludedWorkspaceIdsQueryOptions() {
    return queryOptions(
        "get",
        "/bff/odata/doNotDisturbWorkspaces",
        { params: { query: excludedWorkspaceIdsQuery } },
        { select: selectExcludedWorkspaceIds }
    );
}

function selectCount(data: { "@odata.count"?: number | null }) {
    return data["@odata.count"] ?? 0;
}

function countQueryOptions(
    reviewId: string,
    path: "/bff/odata/reviewNotificationRecipients" | "/bff/odata/reviewSelectedWorkspaces",
    filter: string
) {
    const query = buildODataQuery({
        select: ["id"],
        top: 1,
        count: true,
        filter: `reviewId eq ${reviewId} and ${filter}`
    });

    return queryOptions("get", path, { params: { query } }, { select: selectCount });
}

export function excludedRecipientsCountQueryOptions(reviewId: string) {
    return countQueryOptions(reviewId, "/bff/odata/reviewNotificationRecipients", "isDoNotDisturb eq true");
}

export function excludedWorkspacesCountQueryOptions(reviewId: string) {
    return countQueryOptions(reviewId, "/bff/odata/reviewSelectedWorkspaces", "isDoNotDisturb eq true");
}

export function reviewableWorkspacesCountQueryOptions(reviewId: string) {
    return countQueryOptions(
        reviewId,
        "/bff/odata/reviewSelectedWorkspaces",
        "isDoNotDisturb eq false and hasNoEmailableOwner eq false"
    );
}
